/**
 * Copyright (c) HashiCorp, Inc.
 */

import { assert } from '@ember/debug';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import {
  HdsButton,
  HdsDropdown,
  HdsInteractive,
} from '@hashicorp/design-system-components/components';
import { HdsButtonSizeValues } from '@hashicorp/design-system-components/components/hds/button/types';
import { or } from 'ember-truth-helpers';

import './list-item.scss';
import ListItemActionGeneric from './list-item/action-generic.gts';
import ListItemContent from './list-item/content.gts';

import type { WithBoundArgs } from '@glint/template';
import type { HdsInteractiveSignature } from '@hashicorp/design-system-components/components/hds/interactive/index';
import type { ExternalSource } from '../../utils/external-source';

export interface CutServiceInstance {
  name?: string;
  metadata: {
    healthCheck?: {
      node?: HealthCheck;
      service?: HealthCheck;
    };
    tags?: string[];
    servicePortAddress?: string;
    serviceSocketPath?: string;
    node?: string;
    externalSource?: ExternalSource;
    connectedWithProxy?: boolean;
  };
}

export interface CutService {
  name: string | undefined;
  type?: string;
  metadata?: {
    clusterId?: string;
    partition?: string;
    namespace?: string;
    healthCheck?: {
      instance?: HealthCheck;
    };
    kind?: SERVICE_KIND;
    instanceCount?: number;
    isImported?: boolean;
    isPermissiveMTls?: boolean;
    connectedWithGateway?: boolean;
    connectedWithProxy?: boolean;
    samenessGroup?: string;
    externalSource?: ExternalSource;
    tags: string[];
    upstreamCount?: number;
    linkedServiceCount?: number;
  };
}

export interface HealthCheck {
  success?: number;
  warning?: number;
  critical?: number;
}

export type SERVICE_KIND =
  | ''
  | 'typical'
  | 'destination'
  | 'connect-proxy'
  | 'api-gateway'
  | 'mesh-gateway'
  | 'ingress-gateway'
  | 'terminating-gateway';

export interface ListItemSignature {
  Args: HdsInteractiveSignature['Args'] & {
    onClick?: (...args: unknown[]) => void;
  };
  Blocks: {
    default: [
      {
        Content?: typeof ListItemContent;
        ActionButton?: WithBoundArgs<typeof HdsButton, 'size'>;
        ActionDropdown?: typeof HdsDropdown;
        ActionGeneric?: typeof ListItemActionGeneric;
      },
    ];
  };
  Element: HTMLLIElement | HTMLAnchorElement | HTMLButtonElement;
}

export default class ListItem extends Component<ListItemSignature> {
  get route() {
    const { onClick, route, href } = this.args;

    if (route !== undefined) {
      assert(
        `Cut::ListItem" could have applied either @route, @href or @onClick.`,
        onClick === undefined && href === undefined,
      );
    }

    return route;
  }

  get href() {
    const { onClick, route, href } = this.args;

    if (href !== undefined) {
      assert(
        `Cut::ListItem" could have applied either @href, @route or @onClick.`,
        onClick === undefined && route === undefined,
      );
    }
    return href;
  }

  onClickAction = () => {
    // action handles case when no @onClick handler passed to the component
    this.args.onClick?.();
  };

  <template>
    <li
      class="cut-list-item hds-surface-primary
        {{if (or @route @href @onClick) 'hds-surface-mid' 'hds-surface-base'}}"
      ...attributes
    >
      {{#if (or @route @href @onClick)}}
        {{! cut-link is a class that is used in the structure override classes to avoid applying
      extra styles to the link from structure. (https://github.com/hashicorp/structure/blob/d00c8bf39c22b5490a4c6fa8bae951b934432dfb/packages/pds-ember/app/styles/pds/_overrides.scss) }}
        <HdsInteractive
          class="cut-link cut-list-item__content-container active"
          @href={{this.href}}
          @isRouteExternal={{@isRouteExternal}}
          @route={{this.route}}
          @isHrefExternal={{@isHrefExternal}}
          @query={{@query}}
          @replace={{@replace}}
          @model={{@model}}
          @models={{@models}}
          {{on "click" this.onClickAction}}
          {{! template-lint-disable no-nested-splattributes}}
          ...attributes
        >
          {{yield (hash Content=ListItemContent)}}
        </HdsInteractive>

      {{else}}
        {{yield (hash Content=ListItemContent)}}
      {{/if}}

      {{yield
        (hash
          ActionButton=(component HdsButton size=HdsButtonSizeValues.Small)
          ActionDropdown=HdsDropdown
          ActionGeneric=ListItemActionGeneric
        )
      }}
    </li>
  </template>
}
